// The CHAT RELAY page of the hub CLAN panel (player-reach.md §3.10, R1641).
//
// It is a section of the CLAN rail of its own, after DISCORD, so the two
// pages about Discord carry two names (R1945). The page title names it and
// its summary carries the state word, so the page draws its content
// alone, with no card round it. The words "the card" below mean this
// content.
// The owner or an officer of a clan sets one chat relay: the meta server
// then copies each new line of the clan chat to one Discord channel, one
// way only. The card holds the switch of the relay at its top (R2422), the
// URL and the language of the words of the relay. SAVE sends the three
// together
// (`PUT /api/clan/discord/relay`); a change of the switch or of the
// language waits in the card until SAVE. An empty URL box keeps the stored
// URL, which the server answers masked and never whole. TEST posts one
// test message now and says what Discord answered, in the words of the
// DISCORD card. REMOVE deletes the relay, and it takes the second press of
// the armed-control rule (R837).
//
// The state pill reads NOT SET, ON or OFF. OFF is the switch, or a relay
// that Discord refused `failures` times in a row (a deleted webhook); the
// card then says so and asks for the URL of a new one.
//
// A member does not see the card: the CLAN panel mounts it for the owner
// and the officers alone, the same rank the server checks. Every member
// sees the → DISCORD mark of the chat instead (`ui/relaymark.ts`).
//
// The card owns no call state of its own. The CLAN panel runs every write
// through its one `call`, and `chatRelayRefusalFor` gives the line of the
// one refusal of its own; `discord_invalid` takes the line of the DISCORD
// card.

import {
  CODE_RELAY_NONE,
  deleteChatRelay, getChatRelay, putChatRelay, testChatRelay,
  type ChatRelay,
} from '../../../net/api';
import { hubChatRelay as S } from '../../../gen/strings';
import type { ArmGate } from '../../arm';
import { TTL_ON_OPEN, type Cell } from '../hubstate';
import {
  armedPress, ctlRow, errLine, esc, lede, pending, plain, srow, switchButton,
  type PanelContext,
} from '../panel';
import {
  postLangChoice, postLangOf, refused, switchOn, testLine, urlForm, webhookControls, type DiscordHooks,
} from './discord';

/** The cache key of the read. A write drops it. */
const RELAY_CELL = 'clan-chat-relay';

/** The `data-in` name of the URL box. */
export const RELAY_URL_BOX = 'relay-url';

/** What the panel says about one refusal of a relay route, or '' for a
 *  code that is not its own. */
export function chatRelayRefusalFor(code: string): string {
  return code === CODE_RELAY_NONE ? S.refusal_none : '';
}

/** The words of the state pill. A state this build does not know reads
 *  as NOT SET. */
const STATE_WORDS: ReadonlyMap<string, string> = new Map([
  ['on', S.state_on],
  ['off', S.state_off],
]);

/** The card, as the CLAN panel mounts it. */
export interface ChatRelayCard {
  /** The body of the CHAT RELAY page, or '' for a caller who may not see
   *  it. */
  html(manage: boolean): string;
  /** The state word of the relay (NOT SET, ON or OFF), or '' while the
   *  read has not answered: the CHAT RELAY summary of the CLAN rail and of
   *  the page title. */
  state(): string;
  /** Takes one press. Answers false for an action that is not the card's. */
  act(action: string, el: HTMLElement): boolean;
}

/** The edits that wait for SAVE. Null while the card shows the server. */
interface Draft {
  lang: string;
  on: boolean;
}

export function createChatRelayCard(ctx: PanelContext, gate: ArmGate, hooks: DiscordHooks): ChatRelayCard {
  /** The stored relay, through the hub cache. */
  const read = (): Cell<ChatRelay> => ctx.data.cell<ChatRelay>(RELAY_CELL, TTL_ON_OPEN, () => getChatRelay());
  let draft: Draft | null = null;

  /** The draft, or the stored settings when nothing waits. */
  function current(v: ChatRelay): Draft {
    return draft ?? { lang: v.lang, on: switchOn(v) };
  }

  function drop(): void {
    draft = null;
    ctx.data.drop(RELAY_CELL);
  }

  return {
    state(): string {
      const v = read().value;
      return v === undefined ? '' : STATE_WORDS.get(v.state) ?? S.state_none;
    },

    html(manage: boolean): string {
      if (!manage) return '';
      const cell = read();
      const v = cell.value;
      if (v === undefined) return plain(pending(cell), 'chatrelay');
      const d = current(v);
      const url = urlForm(hooks, RELAY_URL_BOX, S.url_placeholder, S.url_label, v.url ? S.url_stored({ url: v.url }) : '');
      return plain(srow(S.switch, esc(S.switch_desc), switchButton(d.on, ' data-act="relay-switch"', S.switch))
        + lede(S.lede)
        + (refused(v) ? errLine(S.off_line({ count: v.failures })) : '')
        + srow(S.url, '', '') + url
        + srow(S.lang, esc(S.lang_desc), '') + ctlRow(postLangChoice(d.lang, 'relay-lang', S.lang))
        + lede(S.save_hint)
        + ctlRow(webhookControls(gate, 'relay', v.configured, S))
        + lede(S.privacy),
        'chatrelay');
    },

    act(action: string, el: HTMLElement): boolean {
      const v = ctx.data.peek<ChatRelay>(RELAY_CELL).value;
      switch (action) {
        case 'relay-switch': {
          if (v === undefined) return true;
          const d = current(v);
          draft = { lang: d.lang, on: !d.on };
          ctx.play('sfx-menu-toggle');
          ctx.refresh();
          return true;
        }
        case 'relay-lang': {
          const lang = postLangOf(el.dataset.id);
          if (v === undefined || lang === undefined) return true;
          draft = { lang, on: current(v).on };
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return true;
        }
        case 'relay-save': {
          if (v === undefined) return true;
          const d = current(v);
          const url = hooks.typed(RELAY_URL_BOX);
          hooks.run(S.busy_save, () => putChatRelay({ url, lang: d.lang, on: d.on }), () => {
            hooks.clear(RELAY_URL_BOX);
            drop();
            hooks.notice(S.notice_saved);
          });
          return true;
        }
        case 'relay-test':
          hooks.run(S.busy_test, () => testChatRelay(), (out) => {
            ctx.data.drop(RELAY_CELL);
            hooks.notice(testLine(out));
          });
          return true;
        case 'relay-remove':
          if (!armedPress(ctx, gate, 'relay-remove')) return true;
          hooks.run(S.busy_remove, () => deleteChatRelay(), () => {
            drop();
            hooks.notice(S.notice_removed);
          });
          return true;
        default:
          return false;
      }
    },
  };
}
